import { useEffect, useRef, useState, useCallback } from "react";
import { Phone, PhoneOff, Loader2, Volume2, Mic, AlertTriangle, MessageSquare, Sparkles, X } from "lucide-react";
import { api, API } from "../lib/api";
import { toast } from "sonner";

/**
 * Low-latency voice call using OpenAI Realtime API + WebRTC.
 *
 * Mounted only for mode in {active_call, passive_call} AND when
 * /api/realtime/status returns available:true.
 *
 * Lifecycle:
 *  1) GET /api/realtime/instructions/{sessionId}  → per-scenario prompt + voice
 *  2) createOffer() + POST SDP to /api/realtime/sdp  → setRemoteDescription(answer)
 *  3) On data-channel "open": send session.update with instructions + voice
 *     + input_audio_transcription + server VAD turn detection
 *  4) Stream conversation.item.* events → POST /realtime/sessions/{id}/turn
 *     to persist transcripts in MongoDB (used by feedback + analytics)
 */
const RealtimeCall = ({ sessionId, sessionMode, scenarioTitle, onEnd, onTranscriptUpdate }) => {
  const [status, setStatus] = useState("connecting"); // connecting | live | ended | error
  const [error, setError] = useState(null);
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [userSpeaking, setUserSpeaking] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState([]); // {role, content, id, partial?}
  const [muted, setMuted] = useState(false);
  // Live coaching hints — small queue, oldest dropped automatically.
  const [hints, setHints] = useState([]); // [{id, kind, severity, text, ts}]
  const userTurnCounterRef = useRef(0);
  const lastLlmTurnRef = useRef(0);
  const llmInFlightRef = useRef(false);
  const hintIdRef = useRef(0);

  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const audioElRef = useRef(null);
  const streamRef = useRef(null);
  const transcriptRef = useRef(liveTranscript);
  transcriptRef.current = liveTranscript;
  // Track partial-by-item-id to merge deltas:
  const partialsRef = useRef({}); // {item_id: {role, content}}
  // --- Parallel voice analytics: MediaRecorder records user audio in chunks tied
  // to Realtime's input_audio_buffer.speech_started/stopped events. Each finished
  // user turn is sent to /realtime/sessions/{id}/analyze-turn for Whisper-based
  // WPM/pauses/fillers, attached server-side to the matching message via item_id.
  const recorderRef = useRef(null);
  const recorderChunksRef = useRef([]);
  const currentUserItemIdRef = useRef(null);

  const upsertTranscript = useCallback((entry) => {
    setLiveTranscript((prev) => {
      const idx = prev.findIndex((e) => e.id === entry.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], ...entry };
        return copy;
      }
      return [...prev, entry];
    });
  }, []);

  // Known Whisper hallucinations on background noise / silence (DE). The Realtime
  // input_audio_transcription uses whisper-1 which suffers from these.
  const WHISPER_HALLUCINATIONS = [
    "untertitel der amara.org-community",
    "untertitel im auftrag des zdf",
    "untertitelung des zdf",
    "untertitelung aufgrund der amara.org-community",
    "untertitelung der amara.org-community",
    "amara.org-community",
    "vielen dank für ihre aufmerksamkeit.",
    "vielen dank.",
    "danke.",
  ];

  const isHallucination = useCallback((text) => {
    const t = (text || "").toLowerCase().trim().replace(/[.!?…\s]+$/g, "");
    if (!t || t.length < 3) return true;
    return WHISPER_HALLUCINATIONS.some((h) => t === h || t.includes("amara.org"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistTurn = useCallback(async (role, content, extra = {}) => {
    const text = (content || "").trim();
    if (!text) return;
    if (isHallucination(text)) {
      console.info("Filtered Whisper hallucination:", text);
      return;
    }
    try {
      const { data } = await api.post(`/realtime/sessions/${sessionId}/turn`, {
        role, content: text, ...extra,
      });
      onTranscriptUpdate?.(data);
    } catch (e) {
      console.warn("persistTurn failed", e);
    }
  }, [sessionId, onTranscriptUpdate, isHallucination]);

  // --- Side-channel voice analytics --------------------------------------
  const startAnalyticsRecorder = useCallback(() => {
    const stream = streamRef.current;
    if (!stream || recorderRef.current) return;
    try {
      const mimeCandidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
      const mimeType = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m)) || "";
      const opts = mimeType ? { mimeType, audioBitsPerSecond: 24000 } : { audioBitsPerSecond: 24000 };
      const mr = new MediaRecorder(stream, opts);
      recorderChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) recorderChunksRef.current.push(e.data); };
      recorderRef.current = mr;
      mr.start();
    } catch (e) {
      console.warn("Analytics recorder failed to start", e);
    }
  }, []);

  const stopAndUploadAnalytics = useCallback((itemId) => {
    const mr = recorderRef.current;
    recorderRef.current = null;
    if (!mr) return;
    const flush = async () => {
      const usedMime = mr.mimeType || "audio/webm";
      const blob = new Blob(recorderChunksRef.current, { type: usedMime });
      recorderChunksRef.current = [];
      if (blob.size < 2500) return; // skip too-short clips
      const ext = usedMime.includes("mp4") ? "mp4" : usedMime.includes("ogg") ? "ogg" : "webm";
      const form = new FormData();
      form.append("audio", blob, `turn.${ext}`);
      if (itemId) form.append("item_id", itemId);
      try {
        const { data } = await api.post(`/realtime/sessions/${sessionId}/analyze-turn`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // Voice-based hints (cheap, immediate)
        const vs = data?.voice_stats;
        if (vs) emitVoiceHints(vs);
      } catch (e) {
        // Non-fatal — analytics is a side-channel, don't surface errors.
        console.warn("analyze-turn upload failed", e);
      }
    };
    try {
      mr.onstop = flush;
      if (mr.state !== "inactive") mr.stop();
      else flush();
    } catch (e) {
      console.warn("recorder stop failed", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // --- Live coaching --------------------------------------------------------
  const pushHint = useCallback((hint) => {
    const id = ++hintIdRef.current;
    const item = { id, ts: Date.now(), ...hint };
    setHints((prev) => {
      // Cap to 3 simultaneous hints, oldest first dropped.
      const next = [...prev, item];
      return next.slice(-3);
    });
    // Persist voice-based hints to the session log (LLM tips are persisted
    // server-side by the /coach endpoint already — skip those to avoid dupes).
    if (hint.kind === "voice") {
      api.post(`/realtime/sessions/${sessionId}/log-hint`, {
        kind: hint.kind, severity: hint.severity, text: hint.text,
      }).catch((e) => console.warn("log-hint failed", e));
    }
    // Auto-fade after 14s — long enough to read, short enough to stay current.
    setTimeout(() => {
      setHints((prev) => prev.filter((h) => h.id !== id));
    }, 14000);
  }, [sessionId]);

  const dismissHint = useCallback((id) => {
    setHints((prev) => prev.filter((h) => h.id !== id));
  }, []);

  // Coaching is only useful when the USER is the seller making the outbound call.
  // In active_call mode the AI is the seller and the user just plays the customer
  // → no coaching needed.
  const coachingEnabled = sessionMode === "passive_call";

  const emitVoiceHints = useCallback((vs) => {
    if (!coachingEnabled || !vs) return;
    if (vs.wpm > 185) {
      pushHint({ kind: "voice", severity: "warn", text: `Tempo zu hoch (${vs.wpm} WPM) — sprich bewusster.` });
    } else if (vs.wpm > 0 && vs.wpm < 95) {
      pushHint({ kind: "voice", severity: "info", text: `Eher langsam (${vs.wpm} WPM) — etwas mehr Energie hilft.` });
    }
    if (vs.filler_count >= 4) {
      pushHint({ kind: "voice", severity: "warn", text: `${vs.filler_count}× Füllwörter — kurz Pause statt „äh".` });
    }
    if (vs.long_pause_count >= 2) {
      pushHint({ kind: "voice", severity: "info", text: "Mehrere lange Pausen — halte das Tempo." });
    }
  }, [pushHint, coachingEnabled]);

  const requestLlmCoaching = useCallback(async () => {
    if (!coachingEnabled) return;
    if (llmInFlightRef.current) return;
    llmInFlightRef.current = true;
    try {
      const { data } = await api.post(`/realtime/sessions/${sessionId}/coach`, {});
      if (data?.tip) {
        pushHint({
          kind: "content",
          severity: data.severity || "info",
          text: data.tip,
        });
      }
    } catch (e) {
      console.warn("coach request failed", e);
    } finally {
      llmInFlightRef.current = false;
    }
  }, [sessionId, pushHint, coachingEnabled]);

  const teardown = useCallback(() => {
    // ORDER MATTERS to avoid the "echo tail" on hangup:
    //  1) Mute + detach the remote audio element IMMEDIATELY — stops any
    //     remaining buffered AI audio from playing through the speakers.
    //  2) Stop the mic tracks so the (now uncancelled) speaker tail can't
    //     loop back into the next session via the OS audio buffer.
    //  3) Stop all PC receiver/sender tracks explicitly.
    //  4) Close the data channel and peer connection.
    if (audioElRef.current) {
      const el = audioElRef.current;
      try { el.muted = true; } catch (e) { /* noop */ }
      try { el.pause(); } catch (e) { /* noop */ }
      try { el.srcObject = null; } catch (e) { /* noop */ }
      try { el.removeAttribute("src"); el.load(); } catch (e) { /* noop */ }
      audioElRef.current = null;
    }
    // Stop side-channel analytics recorder
    if (recorderRef.current) {
      try { if (recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch (e) { /* noop */ }
      recorderRef.current = null;
      recorderChunksRef.current = [];
    }
    // Stop microphone tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => {
        try { t.enabled = false; } catch (e) { /* noop */ }
        try { t.stop(); } catch (e) { /* noop */ }
      });
      streamRef.current = null;
    }
    // Stop every track on the peer connection (senders + receivers)
    if (pcRef.current) {
      try {
        pcRef.current.getSenders().forEach((s) => { try { s.track?.stop(); } catch (e) { /* noop */ } });
        pcRef.current.getReceivers().forEach((r) => { try { r.track?.stop(); } catch (e) { /* noop */ } });
      } catch (e) { /* noop */ }
    }
    try { dcRef.current?.close(); } catch (e) { /* noop */ }
    try { pcRef.current?.close(); } catch (e) { /* noop */ }
    dcRef.current = null;
    pcRef.current = null;
    setAiSpeaking(false);
    setUserSpeaking(false);
  }, []);

  // --- Data-channel event router (Realtime API events) ---------------------
  const handleEvent = useCallback((evt) => {
    const t = evt.type;
    // User audio transcript (Whisper-1 on user mic, configured via session.update)
    if (t === "conversation.item.input_audio_transcription.completed") {
      const id = `u-${evt.item_id}`;
      const content = evt.transcript || "";
      partialsRef.current[id] = null;
      if (isHallucination(content)) {
        // Remove the partial bubble too — Whisper just hallucinated on silence.
        setLiveTranscript((prev) => prev.filter((e) => e.id !== id));
        setUserSpeaking(false);
        // Also drop any analytics audio we recorded for this hallucinated turn.
        recorderChunksRef.current = [];
        const mr = recorderRef.current;
        if (mr) {
          try { if (mr.state !== "inactive") mr.stop(); } catch (err) { /* noop */ }
          recorderRef.current = null;
        }
        return;
      }
      upsertTranscript({ id, role: "user", content, partial: false });
      persistTurn("user", content, { item_id: evt.item_id });
      // Stop the parallel recorder and upload the audio for voice analytics.
      stopAndUploadAnalytics(evt.item_id);
      // Live LLM-coaching every 3rd user turn (skip the first to give context to build).
      userTurnCounterRef.current += 1;
      const n = userTurnCounterRef.current;
      if (n >= 2 && (n - lastLlmTurnRef.current) >= 3) {
        lastLlmTurnRef.current = n;
        // Fire and forget — coaching is a side channel.
        requestLlmCoaching();
      }
      currentUserItemIdRef.current = null;
      setUserSpeaking(false);
      return;
    }
    if (t === "conversation.item.input_audio_transcription.delta") {
      const id = `u-${evt.item_id}`;
      const prev = partialsRef.current[id]?.content || "";
      const next = prev + (evt.delta || "");
      partialsRef.current[id] = { role: "user", content: next };
      // Don't show partial-hallucinations in the UI either.
      if (isHallucination(next)) return;
      upsertTranscript({ id, role: "user", content: next, partial: true });
      return;
    }
    // AI text/audio response transcript (streamed token-wise)
    if (t === "response.audio_transcript.delta") {
      const id = `a-${evt.item_id || evt.response_id}`;
      const prev = partialsRef.current[id]?.content || "";
      const next = prev + (evt.delta || "");
      partialsRef.current[id] = { role: "assistant", content: next };
      upsertTranscript({ id, role: "assistant", content: next, partial: true });
      setAiSpeaking(true);
      return;
    }
    if (t === "response.audio_transcript.done") {
      const id = `a-${evt.item_id || evt.response_id}`;
      const content = evt.transcript || partialsRef.current[id]?.content || "";
      upsertTranscript({ id, role: "assistant", content, partial: false });
      partialsRef.current[id] = null;
      persistTurn("assistant", content);
      return;
    }
    if (t === "response.audio.done" || t === "response.done") {
      setAiSpeaking(false);
      return;
    }
    if (t === "input_audio_buffer.speech_started") {
      setUserSpeaking(true);
      // Start side-channel recording for voice analytics
      startAnalyticsRecorder();
      return;
    }
    if (t === "input_audio_buffer.speech_stopped") {
      setUserSpeaking(false);
      // Note: we don't stop the recorder here — we wait for the
      // input_audio_transcription.completed event so we have the item_id
      // to attach analytics to the correct message.
      return;
    }
    if (t === "error") {
      console.warn("Realtime event error", evt);
      const errCode = evt.error?.code || "";
      const errMsg = (evt.error?.message || "").toLowerCase();
      // Known non-fatal races: ignore them and keep the call alive.
      //  - "audio content of Xms is already shorter than Yms" → interrupt/truncate race
      //  - "already has an active response" → response.create sent twice
      //  - "conversation_already_has_active_response"
      //  - "input_audio_buffer_commit_empty" → no audio captured yet
      const nonFatalPatterns = [
        "already shorter than",
        "already has an active response",
        "input_audio_buffer_commit_empty",
        "no active response",
        "cancellation failed",
      ];
      const isNonFatal =
        nonFatalPatterns.some((p) => errMsg.includes(p)) ||
        ["conversation_already_has_active_response", "input_audio_buffer_commit_empty"].includes(errCode);
      if (isNonFatal) return;

      const detail = evt.error?.message || "Realtime-Fehler";
      setError(detail);
      setStatus("error");
      return;
    }
  }, [persistTurn, upsertTranscript, isHallucination, startAnalyticsRecorder, stopAndUploadAnalytics, requestLlmCoaching]);

  // --- Connect ------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const connect = async () => {
      try {
        // 1) Build peer connection
        const pc = new RTCPeerConnection();
        pcRef.current = pc;

        // Remote audio
        const audioEl = new Audio();
        audioEl.autoplay = true;
        audioElRef.current = audioEl;
        pc.ontrack = (e) => {
          audioEl.srcObject = e.streams[0];
        };

        // Mic
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        stream.getTracks().forEach((tr) => pc.addTrack(tr, stream));

        // Data channel — config (instructions/voice/VAD) is sent inline with the SDP,
        // so we don't need to send a session.update event here. We only listen for transcripts.
        const dc = pc.createDataChannel("oai-events");
        dcRef.current = dc;
        dc.onopen = () => {
          // For active_call (AI calls the user), AI must speak first — nudge it.
          // For passive_call, user speaks first; semantic_vad triggers auto-response.
          // NOTE: In GA, response.create takes no 'modalities' field — modalities are
          // inherited from the session config (audio.output.voice etc.).
          if (sessionMode === "active_call") {
            try {
              dc.send(JSON.stringify({ type: "response.create" }));
            } catch (err) { console.warn("response.create send failed", err); }
          }
          setStatus("live");
        };

        dc.onmessage = (e) => {
          try {
            const evt = JSON.parse(e.data);
            handleEvent(evt);
          } catch (err) {
            console.warn("Bad realtime event", err, e.data);
          }
        };

        // 2) SDP exchange via backend proxy (which includes the session config)
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        if (cancelled) return;

        const token = localStorage.getItem("novatus_token");
        const resp = await fetch(`${API}/realtime/sdp/${sessionId}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/sdp",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: offer.sdp,
        });
        if (!resp.ok) {
          let detail = `HTTP ${resp.status}`;
          try {
            const errBody = await resp.json();
            detail = errBody.detail || detail;
          } catch (parseErr) {
            const txt = await resp.text().catch(() => "");
            if (txt) detail = txt.slice(0, 180);
          }
          throw new Error(`SDP-Negotiation: ${detail}`);
        }
        const { sdp: answerSdp } = await resp.json();
        await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
      } catch (err) {
        if (cancelled) return;
        console.error("Realtime connect failed", err);
        const detail = err?.response?.data?.detail || err?.message || "Verbindung fehlgeschlagen";
        setError(String(detail));
        setStatus("error");
        toast.error("Live-Anruf fehlgeschlagen", { description: String(detail) });
      }
    };

    connect();
    return () => {
      cancelled = true;
      teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const toggleMute = () => {
    if (!streamRef.current) return;
    const next = !muted;
    streamRef.current.getAudioTracks().forEach((tr) => { tr.enabled = !next; });
    setMuted(next);
  };

  const handleEnd = () => {
    if (status === "ended") return; // prevent double-click
    setStatus("ended");
    teardown();
    onEnd?.();
  };

  // ----- UI ---------------------------------------------------------------
  const statusBar = () => {
    if (status === "connecting") {
      return (
        <div className="text-center" data-testid="realtime-connecting">
          <Loader2 className="w-8 h-8 animate-spin text-novatus-green mx-auto" />
          <p className="text-novatus-text-2 mt-3 text-sm">Verbinde Live-Anruf …</p>
        </div>
      );
    }
    if (status === "error") {
      return (
        <div className="text-center" data-testid="realtime-error">
          <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto" />
          <p className="text-amber-300 mt-3 text-sm font-semibold">Live-Anruf nicht möglich</p>
          <p className="text-novatus-text-3 mt-1 text-xs">{error}</p>
          <button onClick={handleEnd} className="mt-4 btn-ghost-green h-9 px-3 rounded-md text-sm">
            Zurück
          </button>
        </div>
      );
    }
    return (
      <div className="text-center">
        <div className={`mx-auto w-24 h-24 rounded-full flex items-center justify-center transition-colors ${
          aiSpeaking ? "bg-novatus-green/25 border-2 border-novatus-green pulse-green" :
          userSpeaking ? "bg-red-500/15 border-2 border-red-500/60" :
          "bg-novatus-card-2 border border-novatus"
        }`}>
          {aiSpeaking ? <Volume2 className="w-10 h-10 text-novatus-green" /> :
            userSpeaking ? <Mic className="w-10 h-10 text-red-400" /> :
            <Phone className="w-10 h-10 text-novatus-text-2" />}
        </div>
        <p className="mt-4 font-semibold text-white text-base" data-testid="realtime-status-text">
          {aiSpeaking ? "KI spricht" : userSpeaking ? "Du sprichst" : "Live · sprich los"}
        </p>
        <p className="text-xs text-novatus-text-3 mt-1">Echtzeit-Verbindung · GPT-Realtime</p>
      </div>
    );
  };

  return (
    <div className="nv-card flex flex-col overflow-hidden h-[calc(100dvh-84px)] sm:h-[calc(100vh-180px)]" data-testid="realtime-call">
      <div className="border-b border-novatus px-3 sm:px-5 py-2 sm:py-3 flex items-center justify-between gap-2 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-novatus-green/15 border border-novatus-green/40 flex items-center justify-center shrink-0">
            <Phone className="w-4 h-4 text-novatus-green" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-sm text-white truncate">{scenarioTitle}</p>
            <p className="text-xs text-novatus-text-3 truncate">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-novatus-green animate-pulse mr-1" />
              Live · {sessionMode === "active_call" ? "Du spielst Kunde" : "Du spielst Verkäufer"}
            </p>
          </div>
        </div>
        <button
          onClick={handleEnd}
          disabled={status === "ended"}
          className="h-9 px-3 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/40 font-semibold flex items-center gap-1.5 text-xs sm:text-sm shrink-0 disabled:opacity-60"
          data-testid="realtime-end-btn"
        >
          {status === "ended" ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>Wird beendet …</span>
            </>
          ) : (
            <>
              <PhoneOff className="w-3.5 h-3.5" />
              <span>Beenden</span>
            </>
          )}
        </button>
      </div>

      {/* Live transcript stream — visually subdued so the coaching cards are the focus.
          The full transcript is the primary reference in post-call review. */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 space-y-2 relative" data-testid="realtime-transcript">
        {/* Floating live-coaching hints — sticky at the top of the scrollable area */}
        {hints.length > 0 && (
          <div className="sticky top-0 z-10 flex flex-col gap-2 pointer-events-none -mx-1 mb-3 pt-1 pb-2" data-testid="coaching-hints">
            {hints.map((h) => {
              const isWarn = h.severity === "warn";
              const isContent = h.kind === "content";
              return (
                <div
                  key={h.id}
                  className={`pointer-events-auto flex items-start gap-2.5 px-3.5 py-3 rounded-xl backdrop-blur-md border shadow-xl animate-slide-up ${
                    isWarn
                      ? "bg-amber-500/20 border-amber-400/60 text-amber-50"
                      : "bg-novatus-green/20 border-novatus-green/60 text-white"
                  }`}
                  data-testid={`coaching-hint-${h.kind}`}
                >
                  <Sparkles className={`w-4 h-4 shrink-0 mt-0.5 ${isWarn ? "text-amber-200" : "text-novatus-green"}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-[10px] uppercase tracking-wider font-bold mb-0.5 ${
                      isWarn ? "text-amber-200" : "text-novatus-green"
                    }`}>
                      {isContent ? "Coach-Tipp" : "Voice-Hinweis"}
                    </div>
                    <p className="text-sm leading-snug font-medium">{h.text}</p>
                  </div>
                  <button
                    onClick={() => dismissHint(h.id)}
                    className="shrink-0 -mr-1 -mt-1 p-1 rounded-full hover:bg-white/10 opacity-60 hover:opacity-100"
                    aria-label="Tipp schließen"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {liveTranscript.length === 0 && status === "live" ? (
          <p className="text-center text-novatus-text-3 text-sm mt-8">
            <MessageSquare className="w-5 h-5 mx-auto mb-2 opacity-50" />
            Das Gespräch erscheint hier live …
          </p>
        ) : liveTranscript.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] px-3 py-1.5 rounded-2xl text-xs leading-relaxed opacity-60 ${
              m.role === "user" ? "bubble-user rounded-br-sm" : "bubble-ai rounded-bl-sm"
            } ${m.partial ? "opacity-40" : ""}`} data-testid={`realtime-msg-${m.role}`}>
              {m.content || (m.partial ? "…" : "")}
            </div>
          </div>
        ))}
      </div>

      {/* Status pad */}
      <div className="border-t border-novatus px-4 py-4 sm:py-6 flex flex-col items-center gap-3 shrink-0">
        {statusBar()}
        {status === "live" && (
          <div className="flex items-center gap-3 mt-2">
            <button
              onClick={toggleMute}
              className={`h-10 px-4 rounded-lg flex items-center gap-2 text-sm font-semibold border transition-colors ${
                muted
                  ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
                  : "bg-novatus-card-2 border-novatus text-novatus-text-2 hover:text-white"
              }`}
              data-testid="realtime-mute-btn"
            >
              <Mic className="w-3.5 h-3.5" /> {muted ? "Stummgeschaltet" : "Mikro aktiv"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RealtimeCall;
